import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Package, Warehouse, Truck, Storefront, Handshake, ShieldCheck, TrendUp,
  CheckCircle, FileXls, Camera, VideoCamera, Buildings, ArrowRight,
} from "@phosphor-icons/react";
import { SUPPORT_EMAIL } from "@/lib/config";

const reasons = [
  "ageing or slow-moving inventory",
  "discontinued vehicle models",
  "excess purchasing",
  "changes in product mix",
  "warehouse consolidation",
  "dealership restructuring",
  "dealership shutdown or brand exit",
];

const sellers = [
  "Automobile dealerships",
  "Authorized service networks",
  "Spare-parts distributors",
  "Retailers",
  "Stockists",
  "Businesses undergoing restructuring",
  "Dealerships closing or changing brands",
];

const buyers = [
  "Automobile spare-parts traders",
  "Distributors",
  "Retailers",
  "Stockists",
  "Exporters",
  "Bulk inventory buyers",
  "Other automotive businesses",
];

const auctionModes = [
  "Expressions of Interest",
  "Buyer offers",
  "Negotiated deals",
  "Bulk purchase proposals",
  "Physical stock inspections",
  "Structured transaction closure",
];

const sparesHubKnowledge = [
  "Automotive spare-parts inventory",
  "Part numbers and catalogues",
  "Dealership stock",
  "Workshop and retailer demand",
  "B2B spare-parts transactions",
  "Logistics and commercial workflows",
];

function Chip({ children }) {
  return (
    <li className="flex items-start gap-2.5 rounded-sm border border-border bg-white px-4 py-3 text-sm text-ink">
      <CheckCircle weight="fill" size={18} className="mt-0.5 shrink-0 text-teal" />
      <span>{children}</span>
    </li>
  );
}

export default function About() {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-navy text-white bp-grid">
        <div className="mx-auto max-w-6xl px-5 py-20 lg:px-8 lg:py-28">
          <span className="inline-flex items-center gap-2 rounded-full border border-teal/30 bg-teal/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-teal">
            <Buildings weight="fill" size={14} /> About CarPartsAuction.in
          </span>
          <h1 className="mt-5 max-w-3xl font-display text-4xl font-black leading-tight tracking-tight sm:text-5xl lg:text-6xl">
            India's B2B marketplace for <span className="text-brand">bulk car parts</span> inventory
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/70">
            A B2B platform built to help automobile dealerships, distributors, retailers and other spare-parts
            businesses sell slow-moving, surplus, dead and closure stock in bulk. Operated by Iradium Automobiles
            Private Limited — the company behind SparesHub.
          </p>
          <p className="mt-4 max-w-2xl text-white/50">
            Our objective is simple: help automotive businesses unlock value from inventory that is otherwise
            sitting idle in warehouses.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild className="h-12 rounded-sm bg-brand px-7 text-base font-semibold text-white hover:bg-brand-hover" data-testid="about-browse-cta">
              <Link to="/lots">Browse Lots <ArrowRight className="ml-1" /></Link>
            </Button>
            <Button asChild variant="outline" className="h-12 rounded-sm border-white/25 bg-transparent px-7 text-base font-semibold text-white hover:bg-white hover:text-navy" data-testid="about-sell-cta">
              <Link to="/register?role=seller">List Your Stock</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Why we exist */}
      <section className="mx-auto max-w-6xl px-5 py-16 lg:px-8 lg:py-20">
        <div className="grid gap-10 lg:grid-cols-[1fr_1.1fr] lg:items-start">
          <div>
            <h2 className="font-display text-3xl font-black tracking-tight text-navy">Why CarPartsAuction.in exists</h2>
            <div className="mt-3 h-1 w-16 rounded-full bg-brand" />
            <p className="mt-6 leading-relaxed text-subtext">
              Automobile businesses routinely accumulate spare parts that become difficult to sell through normal
              channels. Such inventory ties up working capital, occupies warehouse space and becomes harder to
              liquidate over time. CarPartsAuction.in provides a dedicated platform where such stock can be
              presented as bulk lots to relevant B2B buyers.
            </p>
          </div>
          <ul className="grid gap-3 sm:grid-cols-2">
            {reasons.map((r) => <Chip key={r}>{r}</Chip>)}
          </ul>
        </div>
      </section>

      {/* Built for bulk selling */}
      <section className="border-y border-border bg-navy-50">
        <div className="mx-auto max-w-6xl px-5 py-16 lg:px-8 lg:py-20">
          <h2 className="font-display text-3xl font-black tracking-tight text-navy">Built for bulk selling</h2>
          <div className="mt-3 h-1 w-16 rounded-full bg-brand" />
          <p className="mt-5 max-w-2xl text-subtext">
            The platform is designed around complete stock lots rather than individual retail transactions.
            Sellers can list inventory by providing:
          </p>
          <div className="mt-8 grid gap-5 sm:grid-cols-3">
            {[
              { icon: FileXls, title: "Stock list in Excel", desc: "An inventory list in the prescribed Excel format." },
              { icon: Camera, title: "6 clear photographs", desc: "Clear photos of the inventory to be liquidated." },
              { icon: VideoCamera, title: "1 walkthrough video", desc: "A short video walkthrough of the stock or warehouse." },
            ].map((c) => (
              <div key={c.title} className="rounded-sm border border-border bg-white p-6">
                <div className="flex h-11 w-11 items-center justify-center rounded-sm bg-brand/10 text-brand"><c.icon size={22} weight="fill" /></div>
                <h3 className="mt-4 font-display text-lg font-bold text-navy">{c.title}</h3>
                <p className="mt-1.5 text-sm text-subtext">{c.desc}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 max-w-2xl text-sm text-subtext">
            This gives prospective buyers enough information to evaluate the opportunity before expressing interest
            or arranging an inspection.
          </p>
        </div>
      </section>

      {/* Who can sell / who buys */}
      <section className="mx-auto max-w-6xl px-5 py-16 lg:px-8 lg:py-20">
        <div className="grid gap-10 lg:grid-cols-2">
          <div>
            <div className="flex items-center gap-3">
              <Storefront weight="fill" size={24} className="text-brand" />
              <h2 className="font-display text-2xl font-black tracking-tight text-navy">Who can sell</h2>
            </div>
            <p className="mt-4 text-subtext">CarPartsAuction.in is designed for businesses such as:</p>
            <ul className="mt-5 space-y-3">{sellers.map((s) => <Chip key={s}>{s}</Chip>)}</ul>
            <p className="mt-5 text-sm text-subtext">
              For dealership shutdowns or major stock-clearance situations, our team can provide additional support
              in organizing and presenting the inventory for bulk liquidation.
            </p>
          </div>
          <div>
            <div className="flex items-center gap-3">
              <Handshake weight="fill" size={24} className="text-darkteal" />
              <h2 className="font-display text-2xl font-black tracking-tight text-navy">Who buys</h2>
            </div>
            <p className="mt-4 text-subtext">Lots may be evaluated by relevant B2B buyers such as:</p>
            <ul className="mt-5 space-y-3">{buyers.map((b) => <Chip key={b}>{b}</Chip>)}</ul>
            <p className="mt-5 text-sm text-subtext">
              The objective is to bring serious buyers and sellers together for commercially viable bulk transactions.
            </p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-y border-border bg-navy text-white">
        <div className="mx-auto max-w-6xl px-5 py-16 lg:px-8 lg:py-20">
          <h2 className="font-display text-3xl font-black tracking-tight">How the platform works</h2>
          <div className="mt-3 h-1 w-16 rounded-full bg-brand" />
          <div className="mt-8 grid gap-5 md:grid-cols-4">
            {[
              { icon: Package, t: "List a lot", d: "A seller lists a stock lot on CarPartsAuction.in." },
              { icon: TrendUp, t: "Buyers engage", d: "Buyers review inventory, submit an EOI and request an inspection." },
              { icon: Handshake, t: "We coordinate", d: "Our team may assist with buyer coordination, inspections and negotiations." },
              { icon: ShieldCheck, t: "Escrow closure", d: "Eligible deals can be structured using Escrow for confidence on both sides." },
            ].map((s, i) => (
              <div key={s.t} className="rounded-sm border border-white/10 bg-white/5 p-6">
                <div className="flex items-center justify-between">
                  <div className="flex h-11 w-11 items-center justify-center rounded-sm bg-brand/20 text-brand"><s.icon size={22} weight="fill" /></div>
                  <span className="stat text-3xl font-black text-white/15">0{i + 1}</span>
                </div>
                <h3 className="mt-4 font-display text-lg font-bold">{s.t}</h3>
                <p className="mt-1.5 text-sm text-white/60">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* More than an auction */}
      <section className="mx-auto max-w-6xl px-5 py-16 lg:px-8 lg:py-20">
        <div className="grid gap-10 lg:grid-cols-[1fr_1.1fr] lg:items-start">
          <div>
            <h2 className="font-display text-3xl font-black tracking-tight text-navy">More than an auction</h2>
            <div className="mt-3 h-1 w-16 rounded-full bg-brand" />
            <p className="mt-6 leading-relaxed text-subtext">
              Despite the name, CarPartsAuction.in is not limited to traditional auctions. Depending on the lot,
              transactions may take different forms. This flexibility is particularly important for liquidation
              inventory, where condition, quantity, age and commercial viability can differ significantly from lot
              to lot.
            </p>
          </div>
          <ul className="grid gap-3 sm:grid-cols-2">
            {auctionModes.map((m) => <Chip key={m}>{m}</Chip>)}
          </ul>
        </div>
      </section>

      {/* Powered by SparesHub */}
      <section className="border-y border-border bg-palered">
        <div className="mx-auto max-w-6xl px-5 py-16 lg:px-8 lg:py-20">
          <div className="grid gap-10 lg:grid-cols-[1fr_1.1fr] lg:items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-brand/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-brand">
                <Warehouse weight="fill" size={14} /> Powered by SparesHub
              </span>
              <h2 className="mt-4 font-display text-3xl font-black tracking-tight text-navy">Backed by real industry experience</h2>
              <p className="mt-5 leading-relaxed text-subtext">
                CarPartsAuction.in is backed by the experience and industry network of SparesHub, which operates in
                the Indian automotive spare-parts ecosystem. This gives the platform a practical understanding of:
              </p>
            </div>
            <ul className="grid gap-3 sm:grid-cols-2">
              {sparesHubKnowledge.map((k) => <Chip key={k}>{k}</Chip>)}
            </ul>
          </div>
        </div>
      </section>

      {/* Mission + CTA */}
      <section className="mx-auto max-w-4xl px-5 py-20 text-center lg:px-8">
        <h2 className="font-display text-3xl font-black tracking-tight text-navy sm:text-4xl">Our mission</h2>
        <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-subtext">
          To make automotive inventory liquidation faster, more organized and more transparent. We want businesses
          to be able to turn idle inventory into working capital rather than allowing valuable stock to remain
          unused for years.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-x-8 gap-y-3 text-lg font-bold text-navy">
          <span className="flex items-center gap-2"><Truck weight="fill" className="text-brand" /> Sell stock in bulk</span>
          <span className="flex items-center gap-2"><TrendUp weight="fill" className="text-darkteal" /> Unlock working capital</span>
          <span className="flex items-center gap-2"><Package weight="fill" className="text-brand" /> Clear dead inventory</span>
        </div>
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <Button asChild className="h-12 rounded-sm bg-brand px-8 text-base font-semibold text-white hover:bg-brand-hover" data-testid="about-list-cta">
            <Link to="/register?role=seller">List Your Stock</Link>
          </Button>
          <Button asChild variant="outline" className="h-12 rounded-sm border-navy/20 px-8 text-base font-semibold text-navy hover:bg-navy hover:text-white" data-testid="about-contact-cta">
            <Link to="/contact">Contact Us</Link>
          </Button>
        </div>
      </section>

      {/* Company info */}
      <section className="border-t border-border bg-navy-50">
        <div className="mx-auto max-w-6xl px-5 py-12 lg:px-8">
          <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
            <div>
              <h3 className="font-display text-sm font-semibold uppercase tracking-wider text-subtext">Company information</h3>
              <p className="mt-3 font-display text-lg font-bold text-navy">Iradium Automobiles Private Limited</p>
              <p className="text-subtext">Pune, Maharashtra, India</p>
              <p className="mt-2 text-sm text-subtext">
                Email: <a href={`mailto:${SUPPORT_EMAIL}`} className="font-semibold text-brand hover:underline">{SUPPORT_EMAIL}</a>
              </p>
            </div>
            <div className="rounded-sm border border-border bg-white px-5 py-4 text-sm">
              <p className="font-semibold text-navy">CarPartsAuction.in by SparesHub</p>
              <p className="mt-1 text-subtext">Helping the automotive trade unlock value from idle inventory.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
